import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ReactNode } from "react";

import { ClientContext } from "./ClientContext";

import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { instance } from "@/lib/axios/instance";
import { useAuth } from "@/lib/hooks/useAuth";
import { ClientFiltersResponse } from "@/types/response/ClientFiltersResponse";
import { ClientFilter } from "@/types/response/ClientType";
import { GetClientResponse } from "@/types/response/GetClientResponse";
import { GetClientsResponse } from "@/types/response/GetClientsResponse";

type ClientProviderProps = {
  children: ReactNode;
  clientId?: string;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  tenantId?: string | null;
} & ClientFilter;

export const ClientProvider = ({
  children,
  clientId,
  clientType,
  descending,
  orderBy,
  page,
  pageSize = 10,
  search,
  tenantId,
  paymentType,
}: ClientProviderProps) => {
  const { context } = useAuth();
  const {
    data: clients,
    isLoading,
    isFetching,
  }: UseQueryResult<GetClientsResponse, AxiosError> = useQuery({
    queryKey: clientQueryKeys.list({
      clientType,
      descending,
      orderBy,
      page,
      pageSize,
      paymentType,
      search,
      tenantId,
    }),
    queryFn: () =>
      instance
        .get(`/api/v2/Clients`, {
          params: {
            clientType,
            descending,
            orderBy,
            page,
            pageSize,
            paymentType,
            search,
            tenantId: tenantId || undefined,
          },
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    enabled: context === "admin",
    staleTime: 20 * 60 * 1000,
  });

  const {
    data: client,
    isLoading: clientLoading,
    isFetching: clientFetching,
    failureReason,
  }: UseQueryResult<GetClientResponse, AxiosError> = useQuery({
    queryKey: clientQueryKeys.detail(clientId ?? ""),
    queryFn: () =>
      instance.get(`/api/v2/Clients/${clientId}`).then((res) => res.data),
    refetchOnMount: true,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled: !!clientId,
  });

  const {
    data: filters,
    isLoading: filtersLoading,
    isFetching: filtersFetching,
  }: UseQueryResult<ClientFiltersResponse, AxiosError> = useQuery({
    queryKey: clientQueryKeys.filter(tenantId, search),
    queryFn: () =>
      instance
        .get(`/api/v2/Clients/Filters`, {
          params: {
            tenantId: tenantId || undefined,
            search,
          },
        })
        .then((res) => res.data),
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled: context === "admin",
  });

  const value = {
    filters,
    filtersFetching,
    filtersLoading,
    client,
    clientError: failureReason,
    clientFetching,
    clientLoading,
    clients,
    isFetching,
    isLoading,
  };

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
};
